import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';

import type {Nullable} from '../../../utils/utility-types';
import type {CachedMediaFactory} from '../CachedMedia/CachedMedia.factory';

import {logger} from '../../../utils/logger';
import {FlagManager} from '../../../utils/store';
import {isDefined} from '../../../utils/typecheck';
import {BaseAudioModel} from './BaseAudio.model';

export class AudioModel extends BaseAudioModel {
  currentTime = 0;
  playbackRate = 1;
  volume = 0.5;
  private animationFrameId: Nullable<number> = null;
  private audio: Nullable<HTMLAudioElement> = null;
  private readonly DURATION_RETRY_INTERVAL = 100;
  private durationRetryTimeout = 0;
  private durationValue = 0;
  private isDurationReady = new FlagManager(false);
  private isEndedFlag = new FlagManager(false);
  private isPausedFlag = new FlagManager(true);
  private readyState: Nullable<number> = null; // @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState

  constructor(
    props: BaseAudioModel.Props,
    cachedMediaFactory: CachedMediaFactory,
  ) {
    super(props, cachedMediaFactory);

    this.makeBindings();

    makeObservable<AudioModel, 'audio' | 'durationValue' | 'readyState'>(this, {
      audio: observable,
      currentTime: observable,
      duration: computed,
      durationValue: observable,
      init: action.bound,
      isEnded: computed,
      isLoaded: computed,
      isPaused: computed,
      pause: action.bound,
      play: action.bound,
      playbackRate: observable,
      readyState: observable,
      seek: action.bound,
      setPlaybackRate: action.bound,
      setVolume: action.bound,
      volume: observable,
    });

    this.setupReactions();
  }

  override destruct() {
    super.destruct();

    clearTimeout(this.durationRetryTimeout);

    this.stopCurrentTimeTracking();

    this.removeListeners();

    runInAction(() => {
      this.audio = null;
    });
  }

  get duration() {
    return this.durationValue;
  }

  get isEnded() {
    return this.isEndedFlag.value;
  }

  get isLoaded() {
    return (
      this.isDurationReady.isTrue &&
      this.error === null &&
      this.readyState !== null &&
      // @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
      [1, 3, 4].includes(this.audio?.readyState || 0)
    );
  }

  get isPaused() {
    return this.isPausedFlag.value;
  }

  async pause() {
    this.audio?.pause();

    this.props?.onPause?.();
  }

  async play() {
    this.audio?.play();

    this.props?.onPlay?.();

    this.isEndedFlag.setFalse();
  }

  async seek(time: number) {
    if (isDefined(this.audio)) {
      this.audio.currentTime = time;
    }
  }

  async setPlaybackRate(rate: number) {
    if (isDefined(this.audio)) {
      this.audio.playbackRate = rate;

      this.playbackRate = rate;
    }
  }

  setVolume(value: number) {
    if (isDefined(this.audio)) {
      this.volume = value;
      this.audio.volume = value;
    }
  }

  protected override async initAudio() {
    runInAction(() => {
      /**
       * for browser environment src is a string
       */
      const src = this.media.src as string;

      this.audio = new Audio(src);

      this.audio.load();
    });
  }

  protected setupReactions() {
    this.disposer.watch(
      'initAudio',
      reaction(
        () => this.audio,
        player => {
          if (player) {
            this.removeListeners();
            this.addListeners();
          }
        },
      ),
    );
  }

  private addListeners() {
    if (this.audio) {
      this.audio.addEventListener('loadstart', this.onLoadStartHandler);

      this.audio.addEventListener(
        'durationchange',
        this.onDurationChangeHandler,
      );
      this.audio.addEventListener(
        'loadedmetadata',
        this.onDurationChangeHandler,
      );

      this.audio.addEventListener('canplay', this.onHandleReadyState);
      this.audio.addEventListener('canplaythrough', this.onHandleReadyState);
      this.audio.addEventListener('timeupdate', this.timeUpdateHandler);
      this.audio.addEventListener('ended', this.onEndHandler);
      this.audio.addEventListener('pause', this.onPauseHandler);
      this.audio.addEventListener('playing', this.onPlayHandler);
      this.audio.addEventListener('error', this.onErrorHandler);
    }
  }

  private makeBindings() {
    this.onPlayHandler = this.onPlayHandler.bind(this);
    this.onPauseHandler = this.onPauseHandler.bind(this);
    this.onLoadStartHandler = this.onLoadStartHandler.bind(this);
    this.onDurationChangeHandler = this.onDurationChangeHandler.bind(this);
    this.timeUpdateHandler = this.timeUpdateHandler.bind(this);
    this.onEndHandler = this.onEndHandler.bind(this);
    this.onErrorHandler = this.onErrorHandler.bind(this);
    this.onHandleReadyState = this.onHandleReadyState.bind(this);
  }

  private onDurationChangeHandler(event) {
    const prevDuration = this.durationValue;
    const nextDuration = event.currentTarget.duration;

    this.onHandleReadyState();

    /**
     * This is a workaround for getting duration of the audio
     *
     * 1. Set the current time to a very large number
     * 2. After some time set the current time to 0
     * 3. Listen the time update event and call the durationChangeHandler
     */
    if (nextDuration === Infinity || Number.isNaN(nextDuration)) {
      this.audio!.currentTime = 1e10;

      this.retryGetDuration();

      return;
    }

    const shouldUpdate = prevDuration === 0 || prevDuration !== nextDuration;

    if (shouldUpdate) {
      runInAction(() => {
        this.durationValue = nextDuration || 0;

        if (this.isDurationReady.isFalse) {
          this.isDurationReady.setTrue();
        }
      });

      return;
    }

    const shouldTriggerDurationFlag =
      this.isDurationReady.isFalse && prevDuration === nextDuration;

    if (shouldTriggerDurationFlag) {
      this.isDurationReady.setTrue();
    }
  }

  private onEndHandler() {
    runInAction(() => {
      this.currentTime = 0;
      this.isEndedFlag.setTrue();

      this.props?.onEnded?.();
    });
  }

  private onErrorHandler(event: ErrorEvent) {
    const target = event.target as HTMLAudioElement;

    if (target.error) {
      this.setError(new Error(target.error.message));

      const message = `Audio error! Code: ${target.error.code}; details: ${target.error.message}; src: ${this.media.src}`;

      logger.error(new Error(message));
    }
  }

  private onHandleReadyState() {
    runInAction(() => {
      this.readyState = this.audio?.readyState || 0;
    });
  }

  private onLoadStartHandler() {
    if (this.isDurationReady.isTrue) {
      this.isDurationReady.setFalse();
    }
  }

  private onPauseHandler() {
    this.isPausedFlag.setTrue();

    this.stopCurrentTimeTracking();

    this.props?.onPause?.();
  }

  private onPlayHandler() {
    this.isPausedFlag.setFalse();

    this.startCurrentTimeTracking();

    this.props?.onPlay?.();
  }

  private removeListeners() {
    if (!this.audio) {
      return;
    }

    this.audio.pause();

    this.audio.removeEventListener('loadstart', this.onLoadStartHandler);

    this.audio.removeEventListener(
      'durationchange',
      this.onDurationChangeHandler,
    );
    this.audio.removeEventListener(
      'loadedmetadata',
      this.onDurationChangeHandler,
    );

    this.audio.removeEventListener('canplay', this.onHandleReadyState);
    this.audio.removeEventListener('canplaythrough', this.onHandleReadyState);
    this.audio.removeEventListener('timeupdate', this.timeUpdateHandler);
    this.audio.removeEventListener('ended', this.onEndHandler);
    this.audio.removeEventListener('pause', this.onPauseHandler);
    this.audio.removeEventListener('playing', this.onPlayHandler);
    this.audio.removeEventListener('error', this.onErrorHandler);
  }

  private retryGetDuration() {
    clearTimeout(this.durationRetryTimeout);

    this.durationRetryTimeout = +setTimeout(() => {
      if (this.audio) {
        this.audio.currentTime = 0;
      }
    }, this.DURATION_RETRY_INTERVAL);
  }

  private setCurrentTime() {
    runInAction(() => {
      this.currentTime = Number((this.audio?.currentTime ?? 0).toFixed(2));
    });
  }

  private startCurrentTimeTracking() {
    if (this.animationFrameId !== null) {
      return;
    }

    const updateCurrentTime = () => {
      if (!this.audio || this.isPaused) {
        this.animationFrameId = null;

        return;
      }

      this.setCurrentTime();

      this.animationFrameId = requestAnimationFrame(updateCurrentTime);
    };

    this.animationFrameId = requestAnimationFrame(updateCurrentTime);
  }

  private stopCurrentTimeTracking() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private timeUpdateHandler() {
    if (this.isPausedFlag.isTrue) {
      this.setCurrentTime();
    }
  }
}
